import React, { createContext, useContext, useState } from 'react';
import { newrelic } from '../newrelic/newrelic';
import { Feedback } from './feedback';

export enum FeedbackMode {
  Scale,
  Thumbs,
}

export type SupportHint = { text: string; link: string };

interface FeedbackContextModel {
  showFeedbackPanel: () => void;
  hideFeedbackPanel: () => void;
  isFeedbackPanelOpen: boolean;
  setActive: (active: boolean) => void;
  active: boolean;
  setQuestion: (question: string) => void;
  setFeedbackLabels: (negativeLabel: string, positiveLabel: string) => void;
  setFeedbackTextRating: (feedbackTextRating: string) => void;
  setSupportHint: (text: string, link: string) => void;
  showFeedbackBanner: boolean;
  showFeedbackFab: boolean;
  setShowFeedbackBanner: (show: boolean) => void;
  setShowFeedbackFab: (show: boolean) => void;
  feedbackMode: FeedbackMode;
  setFeedbackMode: (mode: FeedbackMode) => void;
  trackingContext: string;
  setTrackingContext: (trackingContext: string) => void;
  setSecondaryButton: (props?: SecondaryBtn) => void;
  setAdditionalFeedbackUrl: (url: string) => void;
  setEventName?: (name: string) => void;
  eventName?: string;
}

export interface SecondaryBtn {
  text: string;
  onClick: () => void;
}

export const FeedbackContext = createContext<FeedbackContextModel | undefined>(undefined);

export const FeedbackContextProvider = ({
  children,
  reverseFeedbackBanner = false,
}: {
  children: React.ReactNode;
  reverseFeedbackBanner?: boolean;
}) => {
  const [eventName, _setEventName] = useState<string>();
  const [showFeedbackPanel, _setShowFeedbackPanel] = useState(false);
  const [active, setActive] = useState(false);
  const [question, _setQuestion] = useState<string>();
  const [feedbackTextRating, _setFeedbackTextRating] = useState<string>();
  const [supportHint, _setSupportHint] = useState<SupportHint>();
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(true);
  const [showFeedbackFab, setShowFeedbackFab] = useState(true);
  const [feedbackLabels, _setFeedbackLabels] = useState<[string, string]>(['', '']);
  const [feedbackMode, setFeedbackMode] = useState<FeedbackMode>(FeedbackMode.Scale);
  const [trackingContext, setTrackingContext] = useState<string>('');
  const [panelContext, bannerContext] = trackingContext ? [`${trackingContext}Panel`, `${trackingContext}Banner`] : ['panel', 'banner'];
  const [secondaryButton, _setSecondaryButton] = useState<SecondaryBtn | undefined>();
  const [additionalFeedbackUrl, setAdditionalFeedbackUrl] = useState<string>();

  const setShowFeedbackPanel = () => {
    newrelic.addPageAction('feedback', {
      context: panelContext,
      triggerName: 'open',
      triggerAction: 'impression',
    });

    _setShowFeedbackPanel(true);
  };

  const hideFeedbackPanel = () => {
    _setShowFeedbackPanel(false);
  };

  const setQuestion = (question: string) => {
    _setQuestion(question);
  };

  const setFeedbackTextRating = (text: string) => {
    _setFeedbackTextRating(text);
  };

  const setFeedbackLabels = (negativeLabel: string, positiveLabel: string) => {
    _setFeedbackLabels([negativeLabel, positiveLabel]);
  };

  const setSecondaryButton = (props: SecondaryBtn) => {
    _setSecondaryButton(props);
  };

  const setSupportHint = (text: string, link: string) => {
    _setSupportHint({ text, link });
  };

  const setEventName = (name: string) => {
    _setEventName(name);
  };

  return (
    <FeedbackContext.Provider
      value={{
        showFeedbackPanel: setShowFeedbackPanel,
        hideFeedbackPanel,
        isFeedbackPanelOpen: showFeedbackPanel,
        setActive,
        active,
        setQuestion,
        setFeedbackTextRating,
        setSupportHint,
        showFeedbackBanner,
        setShowFeedbackBanner,
        setFeedbackLabels,
        feedbackMode,
        setFeedbackMode,
        trackingContext,
        setTrackingContext,
        setSecondaryButton,
        setShowFeedbackFab,
        showFeedbackFab,
        setAdditionalFeedbackUrl,
        setEventName,
        eventName,
      }}
    >
      <Feedback
        showFeedbackBanner={showFeedbackBanner}
        showFeedbackFab={showFeedbackFab}
        setShowFeedbackPanel={_setShowFeedbackPanel}
        question={question}
        active={active}
        feedbackTextRating={feedbackTextRating}
        feedbackLabels={feedbackLabels}
        feedbackMode={feedbackMode}
        trackingContexts={[panelContext, bannerContext]}
        reverse={reverseFeedbackBanner}
        secondaryButton={secondaryButton}
        supportHint={supportHint}
        additionalFeedbackUrl={additionalFeedbackUrl}
        eventName={eventName}
      >
        {children}
      </Feedback>
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);

  if (!context) {
    throw new Error(`useFeedback must be used within a FeedbackContext.Provider`);
  }

  return context;
};
